import type { CSSProperties } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { Runs } from './routes/Runs.js';
import { RunDetail } from './routes/RunDetail.js';
import { RunLogs } from './routes/RunLogs.js';
import { Coverage } from './routes/Coverage.js';

// Fixed colors (not theme tokens): the top bar is always the light Holo chrome,
// so nav links must read on white regardless of the viewer's OS theme.
const navLinkStyle = ({ isActive }: { isActive: boolean }): CSSProperties => ({
  color: isActive ? '#3f0fff' : '#626f86',
  fontWeight: isActive ? 600 : 400,
  textDecoration: 'none',
  fontSize: '0.875rem',
  padding: '4px 10px',
  borderRadius: '6px',
});

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

// MDI logout glyph (arrow exiting a door) — matches the Holo top bar.
function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M14.08,15.59L16.67,13H7V11H16.67L14.08,8.41L15.5,7L20.5,12L15.5,17L14.08,15.59M19,3A2,2 0 0,1 21,5V9.67L19,7.67V5H5V19H19V16.33L21,14.33V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5C3,3.9 3.89,3 5,3H19Z"/>
    </svg>
  );
}

// MDI help-circle glyph.
function HelpIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M15.07,11.25L14.17,12.17C13.45,12.89 13,13.5 13,15H11V14.5C11,13.39 11.45,12.39 12.17,11.67L13.41,10.41C13.78,10.05 14,9.55 14,9C14,7.89 13.1,7 12,7A2,2 0 0,0 10,9H8A4,4 0 0,1 12,5A4,4 0 0,1 16,9C16,9.88 15.64,10.67 15.07,11.25M13,19H11V17H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
    </svg>
  );
}

export function App() {
  const { idToken, email, signIn, signOut, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="center-screen">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  if (!idToken) {
    return (
      <div className="center-screen">
        <div className="signin-card">
          <img src="/borg.png" alt="Borg" className="signin-logo" />
          <p className="signin-subtitle">Run dashboard</p>
          <button onClick={signIn} className="google-btn">
            <GoogleIcon />
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="header-brand">
          <Link to="/" className="brand-link">
            <img src="/borg.png" alt="" className="header-logo" />
            <strong>Borg</strong>
          </Link>
          <nav className="app-nav">
            <NavLink to="/runs" style={navLinkStyle}>Runs</NavLink>
            <NavLink to="/coverage" style={navLinkStyle}>Coverage</NavLink>
          </nav>
        </div>
        <div className="header-who">
          <span className="who-email">Signed in as {email}</span>
          <a
            className="icon-btn"
            href="https://github.com/rewind-corp/borg#readme"
            target="_blank"
            rel="noreferrer"
            title="Help"
            aria-label="Help"
          >
            <HelpIcon />
          </a>
          <button onClick={signOut} className="btn-secondary signout-btn">
            <LogoutIcon />
            <span>Sign out</span>
          </button>
        </div>
      </header>
      <main className="app-main">
        <ErrorBoundary key={location.pathname}>
          <Routes>
            <Route path="/" element={<Navigate to="/runs" replace />} />
            <Route path="/runs" element={<Runs />} />
            <Route path="/runs/:id" element={<RunDetail />} />
            <Route path="/runs/:id/logs" element={<RunLogs />} />
            <Route path="/coverage" element={<Coverage />} />
            <Route path="*" element={<Navigate to="/runs" replace />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
  );
}
